// TODO: Move file to shared hooks?
import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useGridNavigator } from "@/hooks/useGridNavigator";
import { Modifier } from "@/utils/selectInList";

type Arrow = "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown";

type UseGridShortcutsProps = {
  grid: string[][];
  selectedItems?: Set<string>;
  focusedObjectId?: string | null;
  previewOpen?: boolean;
  disabled?: boolean;
  onCopy?: () => void;
  onPreviewToggle?: (next: boolean) => void;
  onPreviewItemChanged?: (direction: "previous" | "next") => void;
  onSelectAll?: () => void;
  onExitContext?: () => void;
  onSelectionChanged?: (newFocusedItem: string, modifier?: Modifier) => void;
  onActivate?: () => void;
};

export const useGridShortcuts = (props: UseGridShortcutsProps): void => {
  const {
    onExitContext,
    onPreviewToggle,
    onPreviewItemChanged,
    onSelectAll,
    onCopy,
    onActivate,
    selectedItems,
    previewOpen = false,
    focusedObjectId,
    disabled,
    onSelectionChanged,
    grid,
  } = props;

  const enabled = !disabled;

  // Handle exit context (escape key)
  useHotkeys(
    "escape",
    () => {
      onExitContext?.();
    },
    {
      preventDefault: true,
      enabled: enabled && !!onExitContext,
    },
    [onExitContext],
  );

  // Handle preview toggle (space key)
  useHotkeys(
    "space",
    () => {
      if (!previewOpen && onPreviewToggle) {
        onPreviewToggle(true);
      }
    },
    {
      preventDefault: true,
      enabled: enabled && !!onPreviewToggle,
    },
    [onPreviewToggle, previewOpen],
  );

  // Handle item activation (enter key)
  useHotkeys(
    "enter",
    () => {
      onActivate?.();
    },
    {
      preventDefault: true,
      enabled: enabled && !!onActivate && selectedItems?.size === 1,
    },
    [onActivate, selectedItems],
  );

  // Handle select all (mod + a)
  useHotkeys(
    "mod+a",
    () => {
      onSelectAll?.();
    },
    {
      preventDefault: true,
      enabled: enabled && !!onSelectAll,
    },
    [onSelectAll],
  );

  // Handle copy (mod + c)
  useHotkeys(
    "mod+c",
    () => {
      onCopy?.();
    },
    {
      enabled: enabled && !!onCopy,
    },
  );
  // Handle arrow navigation (up, down, left, right)
  const { moveDown, moveLeft, moveRight, moveUp } = useGridNavigator({
    grid,
  });

  const handlePreviewArrowNav = useCallback(
    (arrow: Arrow) => {
      switch (arrow) {
        case "ArrowUp":
        case "ArrowLeft":
          onPreviewItemChanged?.("previous");
          break;
        case "ArrowDown":
        case "ArrowRight":
          onPreviewItemChanged?.("next");
          break;
      }
    },
    [onPreviewItemChanged],
  );

  const handleGridArrowSelection = useCallback(
    (arrow: Arrow, modifier?: Modifier) => {
      if (!focusedObjectId) {
        return;
      }

      let newFocusedObjectId = null;

      switch (arrow) {
        case "ArrowUp":
          newFocusedObjectId = moveUp(focusedObjectId);
          break;
        case "ArrowDown":
          newFocusedObjectId = moveDown(focusedObjectId);
          break;
        case "ArrowLeft":
          newFocusedObjectId = moveLeft(focusedObjectId);
          break;
        case "ArrowRight":
          newFocusedObjectId = moveRight(focusedObjectId);
          break;
      }

      if (newFocusedObjectId) {
        onSelectionChanged?.(newFocusedObjectId, modifier);
      }
    },
    [
      focusedObjectId,
      onSelectionChanged,
      moveUp,
      moveDown,
      moveLeft,
      moveRight,
    ],
  );

  useHotkeys(
    ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"],
    (e) => {
      // Sometimes the shift key is considered as an ArrowUp, we want to avoid that
      if (e.key === "Shift") {
        return;
      }

      // When the gallery is open and we have multiple selected objects, we want to navigate
      // through the selected objects instead of the grid
      if (previewOpen && (selectedItems?.size ?? 0) > 1 && !e.shiftKey) {
        handlePreviewArrowNav(e.key as Arrow);
      } else {
        handleGridArrowSelection(
          e.key as Arrow,
          e.shiftKey ? "SHIFT" : undefined,
        );
      }
    },
    {
      preventDefault: true,
      ignoreModifiers: true,
      enabled: enabled && !!focusedObjectId,
    },
    [
      previewOpen,
      selectedItems,
      handlePreviewArrowNav,
      handleGridArrowSelection,
    ],
  );
};
